import React, { useRef } from "react";
import { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, collection, query, where, limit, getDocs } from "firebase/firestore";
import { useLocation, useParams } from "react-router-dom";
import app, { studentAuth } from "../../firebase";

function FinalEnrollment({ student, miscFees = [], cashDiscount = 0, cashWithDiscountDate = "", cashWithoutDiscountDate = "", installmentDates = [] }) {
  const corRef = useRef();

  // Guard: if no student prop provided, show a helpful message and avoid runtime errors
  if (!student) {
    return (
      <div style={{ padding: 28, color: "#444", textAlign: "center" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>No student data</div>
        <div style={{ color: "#666" }}>Finalization is not available — student object is missing.</div>
      </div>
    );
  }

  // Helper for date formatting
  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  // Data extraction
  const studentName = `${student.lastName}, ${student.firstName}`;
  const studentIdNum = student.studentId || student.idNumber || "N/A";
  const program = student.program || "N/A";
  const yearLevel = student.preRegistration?.entryLevel || "N/A";
  const semester = student.preRegistration?.semester || student.semester || "First Semester";
  const schoolYear = student.AY || "2025-2026";

  // Get courses from termSchedules (same logic as CourseLineup component)
  let scheduleArray = [];
  if (student.termSchedules && typeof student.termSchedules === 'object') {
    const availableKeys = Object.keys(student.termSchedules);
    // Get the most recent term key
    const termKey = availableKeys.length > 0 ? availableKeys[availableKeys.length - 1] : null;
    scheduleArray = termKey ? (student.termSchedules[termKey] || []) : [];
  } else if (Array.isArray(student.schedules)) {
    // Fallback to schedules array if termSchedules doesn't exist
    scheduleArray = student.schedules;
  }

  // Group courses
  const groupedCourses = Array.isArray(scheduleArray) && scheduleArray.length > 0
    ? Object.values(
        scheduleArray.reduce((acc, course) => {
          const key = [course.courseId, course.courseName, course.section].join('|');
          if (!acc[key]) {
            acc[key] = {
              courseId: course.courseId,
              courseName: course.courseName,
              section: course.section,
              units: course.units,
              meetings: []
            };
          }
          acc[key].meetings.push({
            day: course.day,
            time: course.time,
            room: course.room
          });
          return acc;
        }, {})
      )
    : [];

  const totalUnits = student.totalUnits
    ? parseFloat(student.totalUnits)
    : Array.isArray(scheduleArray)
      ? scheduleArray.reduce((sum, c) => sum + (parseFloat(c.units) || 0), 0)
      : 0;

  // Calculate fees
  let total = 0;
  const fees = miscFees.map((fee) => {
    let amount = 0;
    let formula = "";
    if (fee.formula?.toLowerCase().includes("units") && typeof fee.amount === "number") {
      amount = totalUnits * fee.amount;
      formula = `(${totalUnits} units X ${fee.amount})`;
    } else if (fee.perUnit !== undefined) {
      amount = totalUnits * fee.perUnit;
      formula = `(${totalUnits} units X ${fee.perUnit})`;
    } else if (fee.formula) {
      formula = fee.formula;
      amount = fee.amount || 0;
    } else {
      amount = fee.amount || 0;
    }
    total += amount;
    return { ...fee, amount, formula };
  });

  const totalWithDiscount = total - cashDiscount;
  const installment = parseFloat((total / 5).toFixed(2));

  // Print handler
  function handlePrint() {
    const printWindow = window.open("", "_blank", "width=900,height=1200");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
      <head>
        <title>Certificate of Registration</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .container { width: 900px; margin: 0 auto; padding: 32px; }
          .header { text-align: center; margin-bottom: 18px; }
          .subtitle { font-size: 20px; margin-bottom: 18px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .subject-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
          .subject-table th, .subject-table td { padding: 6px 8px; font-size: 15px; border-bottom: 1px solid #bbb; }
          .subject-table th { background: #f7f7f7; }
          .subject-table td.day-cell > div, .subject-table td.schedule-cell > div { white-space: pre-line; }
          .fees-table th, .fees-table td { border: none; padding: 6px 8px; font-size: 15px; }
          .fees-table th { background: none; }
          .fees-title { font-weight: bold; font-size: 17px; margin-bottom: 8px; }
          .option-title { font-weight: bold; color: #18803b; font-size: 16px; margin-top: 18px; }
          .note { background: #fff8f1; border: 1px solid #ff9800; border-radius: 6px; padding: 12px; color: #e67e22; font-size: 14px; margin-top: 18px; }
          .print-footer { font-size: 13px; margin-top: 24px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div style="font-size:22px;font-weight:bold;">Manila Adventist College</div>
            <div style="font-size:15px;">CERTIFICATE OF REGISTRATION</div>
            <div class="subtitle">${schoolYear} ${semester}</div>
          </div>
          <div class="info-row">
            <div>Name: <b>${studentName}</b></div>
            <div>Student ID: <b>${studentIdNum}</b></div>
          </div>
          <div class="info-row">
            <div>Program: <b>${program}</b></div>
            <div>Year: <b>${yearLevel}</b></div>
          </div>
          <table class="subject-table">
            <thead>
              <tr>
                <th>Subject Code</th>
                <th>Subject Description</th>
                <th>Section</th>
                <th>Day</th>
                <th>Schedule</th>
                <th>Room</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              ${groupedCourses.map(row => `
                <tr>
                  <td>${row.courseId || ""}</td>
                  <td>${row.courseName || ""}</td>
                  <td>${row.section || ""}</td>
                  <td class="day-cell">
                    ${row.meetings.map(m => `<div>${m.day}</div>`).join("")}
                  </td>
                  <td class="schedule-cell">
                    ${row.meetings.map(m => `<div>${m.time}</div>`).join("")}
                  </td>
                  <td>
                    ${row.meetings.map(m => `<div>${m.room}</div>`).join("")}
                  </td>
                  <td style="text-align:right;">${row.units || ""}</td>
                </tr>
              `).join("")}
              <tr>
                <td colspan="6" style="text-align:right;font-weight:bold;">TOTAL UNITS</td>
                <td style="text-align:right;font-weight:bold;">${totalUnits}</td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top:32px; display:flex; gap:48px;">
            <div style="flex:1;">
              <div style="font-weight:700;font-size:20px;margin-bottom:18px;">MISCELLANEOUS FEES</div>
              <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
                <thead>
                  <tr>
                    <th style="text-align:left;font-weight:500;"> </th>
                    <th style="text-align:right;font-weight:500;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${fees.map(fee => `
                    <tr>
                      <td style="padding:6px 0;">
                        ${fee.label} ${fee.formula ? `<span style='color:#888;font-size:13px;'>${fee.formula}</span>` : ''}
                      </td>
                      <td style="text-align:right;padding:6px 0;">
                        ₱ ${fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  `).join("")}
                  <tr>
                    <td style="font-weight:700;padding-top:10px;">Total:</td>
                    <td style="text-align:right;font-weight:700;font-size:17px;padding-top:10px;">
                      ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style="font-weight:700;color:#18803b;font-size:18px;margin:30px 0 8px 0;">
                OPTION 1: CASH PAYMENT WITH DISCOUNT
              </div>
              <div style="font-size:18px;margin-bottom:4px;font-weight:bold;color:#222;">
                ₱ ${totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size:16px;margin-bottom:18px;">
                If full payment is made on or before ${formatDate(cashWithDiscountDate)}.
              </div>
              <div style="font-weight:700;color:#18803b;font-size:18px;margin:30px 0 8px 0;">
                OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
              </div>
              <div style="font-size:18px;margin-bottom:4px;font-weight:bold;color:#222;">
                ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style="font-size:16px;margin-bottom:18px;">
                If full payment is made after ${formatDate(cashWithoutDiscountDate)}.
              </div>
            </div>
            <div style="flex:1;">
              <div style="font-weight:700;color:#18803b;font-size:20px;margin-bottom:18px;">
                OPTION 3: INSTALLMENT PAYMENT
              </div>
              <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
                <tbody>
                  ${installmentDates.map((date, idx) => `
                    <tr>
                      <td style="padding:6px 0;">
                        ${["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
                      </td>
                      <td style="padding:6px 0;">
                        ${formatDate(date)}
                      </td>
                      <td style="padding:6px 0;">
                        ₱ ${installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  `).join("")}
                  <tr>
                    <td style="font-weight:700;padding-top:10px;">Total:</td>
                    <td></td>
                    <td style="text-align:right;font-weight:700;font-size:17px;padding-top:10px;">
                      ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style="margin-top:32px; font-size:13px; background:#fff8f1; border:1px solid #ff9800; border-radius:6px; padding:12px; color:#e67e22;">
            <div style="font-weight:bold;">IMPORTANT NOTICE:</div>
            <div style="color:#444;">
              1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br>
              2. This registration form serves as the student's proof of enrollment. Students must attend only classes/sections as indicated on the registration form.<br>
              3. This computer-generated REGISTRATION FORM has been electronically approved by the Student, Program Head/SHS Principal, College Treasurer, and College Registrar. No signature required.<br>
            </div>
          </div>
          <div style="font-size:13px; margin-top:24px;">
            Printed on: ${formatDate(new Date().toISOString())}<br>
            Officially Enrolled on: ${formatDate(student.enrolledDate || new Date().toISOString())}
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }

  return (
    <>
      {/* Congratulations Notice - Above Everything */}
      <div style={{ background: "#d4edda", border: "2px solid #18803b", borderRadius: 8, padding: "24px 48px", margin: "40px auto", maxWidth: 1500, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#155724", marginBottom: 8 }}>
            🎉 Congratulations!
          </div>
          <div style={{ fontSize: 15, color: "#155724", lineHeight: 1.6 }}>
            You are now officially enrolled. Please check your email for your Login Credentials and you may proceed to your Student Portal.
          </div>
        </div>
        <a
          href="https://enrollease-19c30.web.app/enrolled/signin"
          style={{
            background: "#18803b",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            borderRadius: 6,
            padding: "12px 32px",
            textDecoration: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginLeft: 24,
            whiteSpace: "nowrap"
          }}
        >
          Login
        </a>
      </div>

      {/* Visible COR Document */}
      <div style={{ maxWidth: 1200, margin: "40px auto", background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.15)", overflow: "hidden" }}>
        {/* Header with Download Button */}
        <div style={{ background: "#18803b", color: "#fff", padding: "24px 48px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
              Certificate of Registration
            </div>
            <div style={{ fontSize: 15, opacity: 0.9 }}>
              You are now officially enrolled
            </div>
          </div>
          <button
            onClick={handlePrint}
            style={{
              background: "#fff",
              color: "#18803b",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 6,
              padding: "12px 32px",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              letterSpacing: 0.5
            }}
          >
            DOWNLOAD / PRINT
          </button>
        </div>

        {/* COR Document - Now Visible and Wider */}
        <div
          ref={corRef}
          style={{
            width: "100%",
            maxWidth: 1100,
            margin: "0 auto",
            padding: "48px 60px",
            background: "#fff",
            fontSize: 14,
            lineHeight: 1.5,
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 32, borderBottom: "2px solid #18803b", paddingBottom: 16 }}>
            <div style={{ fontSize: 26, fontWeight: "bold", color: "#18803b" }}>Manila Adventist College</div>
            <div style={{ fontSize: 17, marginTop: 4 }}>CERTIFICATE OF REGISTRATION</div>
            <div style={{ fontSize: 20, marginTop: 8, fontWeight: 600 }}>
              {schoolYear} {semester}
            </div>
          </div>

          {/* Student Info */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 16 }}>
              <div><span style={{ fontWeight: 500 }}>Name:</span> <b>{studentName}</b></div>
              <div><span style={{ fontWeight: 500 }}>Student ID:</span> <b>{studentIdNum}</b></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
              <div><span style={{ fontWeight: 500 }}>Program:</span> <b>{program}</b></div>
              <div><span style={{ fontWeight: 500 }}>Year:</span> <b>{yearLevel}</b></div>
            </div>
          </div>

          {/* Subjects Table - Wider */}
          <div style={{ marginBottom: 40 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f7f7f7" }}>
                  {["Subject Code", "Subject Description", "Section", "Day", "Schedule", "Room", "Units"].map((h) => (
                    <th key={h} style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, fontWeight: 600, textAlign: "left" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedCourses.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>{row.courseId || ""}</td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>{row.courseName || ""}</td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>{row.section || ""}</td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>
                      {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.day}</div>)}
                    </td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14 }}>
                      {row.meetings.map((m, i) => <div key={i} style={{ whiteSpace: "pre-line" }}>{m.time}</div>)}
                    </td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>
                      {row.meetings.map((m, i) => <div key={i}>{m.room}</div>)}
                    </td>
                    <td style={{ padding: 12, border: "1px solid #ddd", fontSize: 14, textAlign: "center" }}>{row.units || ""}</td>
                  </tr>
                ))}
                <tr style={{ background: "#f7f7f7" }}>
                  <td colSpan={6} style={{ padding: 12, border: "1px solid #ddd", textAlign: "right", fontWeight: 700, fontSize: 15 }}>TOTAL UNITS</td>
                  <td style={{ padding: 12, border: "1px solid #ddd", textAlign: "center", fontWeight: 700, fontSize: 15 }}>{totalUnits}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Fees Section - Wider with More Space */}
          <div style={{ display: "flex", gap: 60, marginBottom: 40 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 19, marginBottom: 16, color: "#18803b", borderBottom: "2px solid #18803b", paddingBottom: 8 }}>
                MISCELLANEOUS FEES
              </div>
              <table style={{ width: "100%", marginBottom: 16 }}>
                <tbody>
                  {fees.map((fee, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>
                        {fee.label}{" "}
                        {fee.formula && <span style={{ color: "#888", fontSize: 13 }}>{fee.formula}</span>}
                      </td>
                      <td style={{ textAlign: "right", padding: "8px 0", fontSize: 14, fontWeight: 500 }}>
                        ₱ {fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #333" }}>
                    <td style={{ fontWeight: 700, paddingTop: 10, fontSize: 15 }}>Total:</td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, marginTop: 24, marginBottom: 8 }}>
                OPTION 1: CASH PAYMENT WITH DISCOUNT
              </div>
              <div style={{ fontSize: 17, fontWeight: "bold", color: "#222", marginBottom: 4 }}>
                ₱ {totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 14, marginBottom: 16, color: "#555" }}>
                If full payment is made on or before {formatDate(cashWithDiscountDate)}.
              </div>

              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 16, marginTop: 16, marginBottom: 8 }}>
                OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
              </div>
              <div style={{ fontSize: 17, fontWeight: "bold", color: "#222", marginBottom: 4 }}>
                ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 14, marginBottom: 16, color: "#555" }}>
                If full payment is made after {formatDate(cashWithoutDiscountDate)}.
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#18803b", fontSize: 19, marginBottom: 16, borderBottom: "2px solid #18803b", paddingBottom: 8 }}>
                OPTION 3: INSTALLMENT PAYMENT
              </div>
              <table style={{ width: "100%" }}>
                <tbody>
                  {installmentDates.map((date, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>
                        {["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
                      </td>
                      <td style={{ padding: "8px 0", fontSize: 14 }}>{formatDate(date)}</td>
                      <td style={{ textAlign: "right", padding: "8px 0", fontSize: 14, fontWeight: 500 }}>
                        ₱ {installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid #333" }}>
                    <td style={{ fontWeight: 700, paddingTop: 10, fontSize: 15 }}>Total:</td>
                    <td></td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Notice */}
          <div style={{ marginTop: 32, fontSize: 13, background: "#fff8f1", border: "1px solid #ff9800", borderRadius: 6, padding: 18, color: "#e67e22" }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>IMPORTANT NOTICE:</div>
            <div style={{ color: "#555", lineHeight: 1.6 }}>
              1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br />
              2. This registration form serves as the student's proof of enrollment. Students must attend only classes/sections as indicated on the registration form.<br />
              3. This computer-generated REGISTRATION FORM has been electronically approved by the Student, Program Head/SHS Principal, College Treasurer, and College Registrar. No signature required.
            </div>
          </div>

          {/* Footer */}
          <div style={{ fontSize: 13, marginTop: 24, paddingTop: 16, borderTop: "1px solid #ddd", color: "#666" }}>
            <div>Printed on: {formatDate(new Date().toISOString())}</div>
            <div>Officially Enrolled on: {formatDate(student.enrolledDate || student.cor?.corPdfGeneratedAt || new Date().toISOString())}</div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function FinalEnrollmentPage({ student }) {
  const location = useLocation();
  const params = useParams();
  const [localStudent, setLocalStudent] = useState(student || location.state?.student || null);

  async function resolveStudentDocByIdentifier(db, identifier) {
    if (!identifier) return null;
    const idStr = String(identifier).trim();

    try {
      const directRef = doc(db, "students", idStr);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) return { id: directSnap.id, ...directSnap.data() };
    } catch (err) {
      // continue
    }

    let q = query(collection(db, "students"), where("idNumber", "==", idStr), limit(1));
    let snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };

    q = query(collection(db, "students"), where("uid", "==", idStr), limit(1));
    snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };

    if (idStr.includes("@")) {
      q = query(collection(db, "students"), where("email", "==", idStr), limit(1));
      snap = await getDocs(q);
      if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }

    return null;
  }

  useEffect(() => {
    if (localStudent) return;

    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore(app);
        const auth = studentAuth;

        const pathSeg = (location.pathname || "").split("/").filter(Boolean).pop();

        const candidates = [
          params.id,
          params.slug,
          pathSeg,
          location.state?.student?.id,
          location.state?.student?.idNumber,
          student?.id,
          student?.idNumber,
          auth?.currentUser?.uid,
          auth?.currentUser?.email
        ].filter(Boolean);

        console.debug("FinalEnrollment: attempting resolve with candidates:", candidates);

        for (const idCandidate of candidates) {
          if (cancelled) return;
          console.debug("FinalEnrollment: trying candidate ->", idCandidate);
          const found = await resolveStudentDocByIdentifier(db, idCandidate);
          console.debug("FinalEnrollment: result for", idCandidate, "=>", !!found, found ? found.id : null);
          if (found) {
            setLocalStudent(found);
            console.log("FinalEnrollment: resolved student by", idCandidate, found.id);
            return;
          }
        }

        console.warn("FinalEnrollment: student not found. candidates:", candidates);
      } catch (err) {
        console.error("FinalEnrollment: failed to resolve student:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [params, location, student, localStudent]);

  const [miscFees, setMiscFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);

  useEffect(() => {
    const fetchFees = async () => {
      const db = getFirestore(app);
      const ref = doc(db, "systemSettings", "miscellaneousFees");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setMiscFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 0);
        setCashWithDiscountDate(data.cashWithDiscountDate || "");
        setCashWithoutDiscountDate(data.cashWithoutDiscountDate || "");
        setInstallmentDates(data.installmentDates || []);
      }
    };
    fetchFees();
  }, []);

  console.log("FinalEnrollment: prop student =", student);
  console.log("FinalEnrollment: location.state.student =", location.state?.student);
  console.log("FinalEnrollment: localStudent =", localStudent);

  if (!localStudent) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#444" }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No student data</div>
          <div style={{ color: "#666" }}>Make sure you navigated here with the student object in location.state.</div>
        </div>
      </div>
    );
  }

  return (
    <FinalEnrollment
      student={localStudent}
      miscFees={miscFees}
      cashDiscount={cashDiscount}
      cashWithDiscountDate={cashWithDiscountDate}
      cashWithoutDiscountDate={cashWithoutDiscountDate}
      installmentDates={installmentDates}
    />
  );
}